import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { trackEvent } from '@/lib/faro';
import type { PaymentSkipRequest } from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { PauseCircle } from 'lucide-react';

const STATUS_COLORS: Record<string, string> = {
    pending: 'bg-yellow-900/30 text-yellow-400',
    approved: 'bg-green-900/30 text-green-400',
    denied: 'bg-red-900/30 text-red-400',
};

export function PaymentSkip({ contractId, isDom, isSub }: { contractId: number; isDom: boolean; isSub: boolean }) {
    const queryClient = useQueryClient();
    const [reason, setReason] = useState('');
    const [denialReason, setDenialReason] = useState('');
    const [error, setError] = useState('');

    const { data } = useQuery({
        queryKey: ['skip-requests', contractId],
        queryFn: () => api.get<{ data: PaymentSkipRequest[] }>(`/contracts/${contractId}/skip-requests`),
    });

    const requestMutation = useMutation({
        mutationFn: () => api.post(`/contracts/${contractId}/skip-requests`, { reason: reason || undefined }),
        onSuccess: () => {
            trackEvent('payment_skip_requested', { contract_id: contractId });
            setReason('');
            setError('');
            queryClient.invalidateQueries({ queryKey: ['skip-requests', contractId] });
        },
        onError: (err: Error) => setError(err instanceof ApiError ? err.message : 'Failed to request skip'),
    });

    const respondMutation = useMutation({
        mutationFn: ({ skipId, action }: { skipId: number; action: 'approve' | 'deny' }) =>
            api.post(`/contracts/${contractId}/skip-requests/${skipId}/respond`, {
                action,
                ...(action === 'deny' && denialReason ? { denial_reason: denialReason } : {}),
            }),
        onSuccess: (_, { action }) => {
            trackEvent('payment_skip_responded', { contract_id: contractId, action });
            setDenialReason('');
            queryClient.invalidateQueries({ queryKey: ['skip-requests', contractId] });
            queryClient.invalidateQueries({ queryKey: ['contract', String(contractId)] });
            queryClient.invalidateQueries({ queryKey: ['ledger', String(contractId)] });
        },
    });

    const requests = data?.data ?? [];
    const hasPending = requests.some(r => r.status === 'pending');

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                    <PauseCircle className="h-4 w-4" />
                    Hard Times Payment Skip
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Sub request form */}
                {isSub && !hasPending && (
                    <div className="space-y-2">
                        <Input
                            placeholder="Reason for skip request (optional)"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                        />
                        {error && <p className="text-xs text-red-400">{error}</p>}
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={requestMutation.isPending}
                            onClick={() => requestMutation.mutate()}
                        >
                            {requestMutation.isPending ? 'Requesting...' : 'Request Payment Skip'}
                        </Button>
                    </div>
                )}

                {requests.length === 0 && (
                    <p className="text-sm text-slate-500 italic">No skip requests yet.</p>
                )}

                {requests.map((req) => (
                    <div key={req.id} className="rounded border border-slate-700 p-3 space-y-2">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Badge className={STATUS_COLORS[req.status]}>
                                    {req.status}
                                </Badge>
                                <span className="text-xs text-slate-400">
                                    {new Date(req.created_at).toLocaleDateString()}
                                </span>
                            </div>
                            {parseFloat(req.skip_fee_charged) > 0 && (
                                <span className="text-xs text-red-400">
                                    Fee: ${parseFloat(req.skip_fee_charged).toFixed(2)}
                                </span>
                            )}
                        </div>
                        {req.reason && (
                            <p className="text-sm text-slate-300 italic">"{req.reason}"</p>
                        )}
                        {req.denial_reason && (
                            <p className="text-sm text-red-400">Denied: {req.denial_reason}</p>
                        )}

                        {/* Dom response form */}
                        {isDom && req.status === 'pending' && (
                            <div className="flex flex-wrap items-center gap-2 pt-1">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-green-800 text-green-400 hover:bg-green-900/30"
                                    disabled={respondMutation.isPending}
                                    onClick={() => respondMutation.mutate({ skipId: req.id, action: 'approve' })}
                                >
                                    Approve
                                </Button>
                                <Input
                                    placeholder="Denial reason"
                                    value={denialReason}
                                    onChange={(e) => setDenialReason(e.target.value)}
                                    className="w-40 text-sm"
                                />
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-red-800 text-red-400 hover:bg-red-900/30"
                                    disabled={respondMutation.isPending}
                                    onClick={() => respondMutation.mutate({ skipId: req.id, action: 'deny' })}
                                >
                                    Deny
                                </Button>
                            </div>
                        )}
                    </div>
                ))}
            </CardContent>
        </Card>
    );
}
